import { Component } from '@theme/component';

/**
 * AnnouncementCountdownBanner
 *
 * Supports two timer modes, chosen in the theme editor
 * (section.settings.timer_type):
 *
 * - "fixed": counts down to a single merchant-configured end date/time
 *   (section.settings.end_datetime, an ISO 8601 string with a UTC offset).
 *   There is no relative duration and no client-side persistence of any
 *   kind: the same absolute timestamp is parsed fresh on every page load,
 *   for every visitor and every device, so refreshing, navigating away and
 *   back, opening a new tab, or opening a private/incognito window never
 *   changes what's being counted down to - it's always
 *   end_datetime - Date.now().
 *
 * - "evergreen": counts down a merchant-configured duration
 *   (section.settings.evergreen_days/hours/minutes) starting the first
 *   time a given visitor sees the banner. The resulting expiry timestamp
 *   is persisted in localStorage, namespaced by
 *   section.settings.evergreen_campaign_id, so refreshing, navigating
 *   between pages, or closing and reopening the tab never grants that
 *   visitor a fresh countdown - they always see the real remaining time
 *   until their original expiry. Once expired, the stored expiry stays in
 *   the past forever, so the countdown never silently restarts. Changing
 *   the duration setting without changing the campaign ID leaves existing
 *   visitors' stored expiry untouched; changing the campaign ID starts an
 *   unrelated countdown (new localStorage key) for everyone.
 *
 * Config is passed in from the section via data attributes (Shopify's
 * equivalent of a co-located config object, editable in the theme editor
 * without touching this file):
 *   data-timer-type           -> "fixed" | "evergreen"
 *   data-end-datetime         -> ISO 8601 end timestamp (fixed mode)
 *   data-evergreen-duration-ms -> countdown duration in ms (evergreen mode)
 *   data-campaign-id          -> localStorage namespace (evergreen mode)
 *   data-expiry-behavior      -> "hide" | "message" (evergreen mode)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [message]
 * @property {HTMLElement} [expiredMessage]
 * @property {HTMLElement} [timer]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementCountdownBanner extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #endTime = 0;

  /** @type {'fixed' | 'evergreen'} */
  #mode = 'fixed';

  /** @type {'hide' | 'message'} */
  #expiryBehavior = 'hide';

  connectedCallback() {
    super.connectedCallback();

    // Defensive: guarantee at most one interval per instance even if
    // Shopify's Theme Editor re-triggers connectedCallback without an
    // intervening disconnectedCallback (e.g. section re-render).
    this.#clearInterval();

    // Browser-only: window is never touched during Liquid's server render,
    // only once this custom element hydrates client-side.
    if (typeof window === 'undefined') return;

    this.#mode = this.dataset.timerType === 'evergreen' ? 'evergreen' : 'fixed';
    this.#expiryBehavior = this.dataset.expiryBehavior === 'message' ? 'message' : 'hide';

    this.#endTime = this.#mode === 'evergreen' ? this.#resolveEvergreenEndTime() : this.#resolveFixedEndTime();

    this.update();

    // Nothing left to tick towards if the end time is missing/invalid or
    // already in the past - avoid starting an interval that has nothing
    // left to do.
    if (!this.#endTime || this.#endTime <= Date.now()) return;

    this.#intervalId = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearInterval();
  }

  #clearInterval() {
    if (this.#intervalId !== undefined) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  /**
   * Fixed mode: parse the merchant's absolute end timestamp. Identical
   * result for every visitor, device, tab, and page load.
   *
   * @returns {number} End time in ms since epoch, or 0 if missing/invalid.
   */
  #resolveFixedEndTime() {
    const parsed = Date.parse(this.dataset.endDatetime ?? '');
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Evergreen mode: read this visitor's stored expiry for the current
   * campaign, or - if they have none yet - compute one from the
   * configured duration and persist it so it survives refreshes,
   * navigation, and new tabs/sessions in the same browser.
   *
   * @returns {number} End time in ms since epoch, or 0 if no usable duration.
   */
  #resolveEvergreenEndTime() {
    const storageKey = this.#evergreenStorageKey();

    const stored = AnnouncementCountdownBanner.#readStoredExpiry(storageKey);
    if (stored !== null) return stored;

    const durationMs = Number(this.dataset.evergreenDurationMs);
    const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 0;
    if (!safeDuration) return 0;

    const expiry = Date.now() + safeDuration;
    AnnouncementCountdownBanner.#writeStoredExpiry(storageKey, expiry);
    return expiry;
  }

  /**
   * @returns {string} A localStorage key namespaced to this evergreen campaign.
   */
  #evergreenStorageKey() {
    const campaignId = (this.dataset.campaignId ?? '').trim() || 'default';
    const safeCampaignId = campaignId.replace(/[^a-zA-Z0-9_-]/g, '-');
    return `picsbydash_countdown_${safeCampaignId}`;
  }

  /**
   * @param {string} key
   * @returns {number | null} A previously stored expiry timestamp, or null
   * if none exists, it's invalid, or localStorage is unavailable.
   */
  static #readStoredExpiry(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return typeof parsed?.expiry === 'number' && Number.isFinite(parsed.expiry) ? parsed.expiry : null;
    } catch {
      // localStorage unavailable/blocked (private mode, disabled, sandboxed
      // iframe) or corrupt JSON - degrade to in-memory only rather than
      // letting the banner break.
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {number} expiry
   */
  static #writeStoredExpiry(key, expiry) {
    try {
      window.localStorage.setItem(key, JSON.stringify({ expiry }));
    } catch {
      // Same graceful degradation as the read path above.
    }
  }

  update() {
    const remaining = Math.max(0, this.#endTime - Date.now());

    if (remaining === 0) {
      // Reached the end time - stop ticking rather than looping into a new
      // cycle. Fixed mode simply holds the display at zero, identically to
      // before. Evergreen mode additionally applies the merchant's expiry
      // behaviour, and never recomputes a new expiry from here.
      this.#clearInterval();

      if (this.#mode === 'evergreen') {
        this.#applyExpiredState();
        return;
      }
    }

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining / (60 * 60 * 1000)) % 24);
    const minutes = Math.floor((remaining / (60 * 1000)) % 60);
    const seconds = Math.floor((remaining / 1000) % 60);

    const { days: daysEl, hours: hoursEl, minutes: minutesEl, seconds: secondsEl } = this.refs;

    daysEl.textContent = String(days).padStart(2, '0');
    hoursEl.textContent = String(hours).padStart(2, '0');
    minutesEl.textContent = String(minutes).padStart(2, '0');
    secondsEl.textContent = String(seconds).padStart(2, '0');
  }

  #applyExpiredState() {
    if (this.#expiryBehavior === 'hide') {
      this.hidden = true;
      return;
    }

    const { message, timer, expiredMessage } = this.refs;
    if (message) message.hidden = true;
    if (timer) timer.hidden = true;
    if (expiredMessage) expiredMessage.hidden = false;
  }
}

if (!customElements.get('announcement-countdown-banner')) {
  customElements.define('announcement-countdown-banner', AnnouncementCountdownBanner);
}
